import {RouteContext} from "../../RouteContext";
import {SearchConditionElement} from "../../../io/SearchConditionElement";
import {
    Button,
    Dialog, DialogActions,
    DialogBody, DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger, makeStyles, Spinner
} from "@fluentui/react-components";
import {SortOrderElement} from "../../../io/SortOrderElement";
import {useError, useUndefined} from "../../../type/States";
import {Column} from "./Layout";
import {
    DocumentArrowDown20Filled,
    DocumentArrowDown20Regular, DocumentOnePageSparkleFilled
} from "@fluentui/react-icons";
import React, {useEffect, useRef, useState} from "react";
import {getCurrentSubDomain} from "../../HomePage";
import {getExcelFileDate} from "../../../utils/formatDate";
import {ShoppingExcelExportDataTransferObject} from "../../../model/dto/content/ShoppingExcelExportDataTransferObject";

const useStyles = makeStyles({
    button: {
        height: '48px',
        justifyContent: 'start'
    }
})

export type SheetButtonUploadMethod = 'POST' | 'PUT'

export type SheetButtonUpload = {
    name: string
    url: string
    method: SheetButtonUploadMethod
}

export type SheetButtonDownload<Table> = (
    searchConditions: SearchConditionElement<Table>[],
    sortOrders: SortOrderElement<Table>[],
    offset: bigint,
    size: number
) => string

export type SheetButtonDownloadExample = {
    name: string
    url: string
}

export type SheetButtonProps<Table> = RouteContext & {
    uploadSheetUrls?: SheetButtonUpload[]
    downloadSheetUrl?: SheetButtonDownload<Table>
    downloadSheetExampleUrls?: { name: string, url: string }[]
    excelExportUrl?: SheetButtonDownload<Table>
    searchConditions: SearchConditionElement<Table>[]
    sortOrders: SortOrderElement<Table>[]
    offset: bigint
    count: number
}

export default function SheetButton<Table>(props: SheetButtonProps<Table>) {
    const inputRef = useRef<HTMLInputElement>(null);
    const fileState = useUndefined<File>()
    const uploadUrlState = useUndefined<SheetButtonUpload>()
    const styles = useStyles()
    const uploadSheetUrls = props.uploadSheetUrls
    const downloadSheetUrl = props.downloadSheetUrl
    const downloadSheetExampleUrls = props.downloadSheetExampleUrls
    const excelExportUrl = props.excelExportUrl
    const uploadErrorState = useError()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const currentSubDomain = getCurrentSubDomain();

    useEffect(() => {
        const file = fileState.value
        const uploadUrl = uploadUrlState.value
        if (file === undefined || uploadUrl === undefined) {
            return
        }

        const formData = new FormData()
        formData.append('file', file)

        setIsLoading(true);

        fetch(uploadUrl.url, { method: uploadUrl.method, body: formData })
            .then(async response => {
                if (response.ok) {
                    alert('파일 업로드 성공')
                    window.location.reload()
                } else {
                    throw new Error(await response.text())
                }
            })
            .catch(uploadErrorState.setDefinedValue)
            .finally(() => setIsLoading(false))
    }, [fileState.value, uploadUrlState.value]);

    if (uploadSheetUrls === undefined && downloadSheetUrl === undefined && downloadSheetExampleUrls === undefined) {
        return <></>
    }

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files === null) {
            fileState.unset()
            return
        }
        if (files.length === 0) {
            fileState.unset()
            return
        }

        const file = files.item(0)
        if (file === null) {
            fileState.unset()
            return
        }

        fileState.setValue(file)
    }

    const onDownloadSheetClick = () => {
        if (downloadSheetUrl === undefined) {
            return
        }

        setIsLoading(true);

        fetch(downloadSheetUrl(props.searchConditions, props.sortOrders, props.offset, 40))
            .then(async value => await value.blob())
            .then(blob => window.URL.createObjectURL(blob))
            .then(url => {
                const a = document.createElement('a')
                a.href = url
                a.download = `${currentSubDomain}-${getExcelFileDate()}.xlsx`
                a.click()
            })
            .finally(()=>setIsLoading(false))
    }

    const onDownloadAllSheetClick = () => {
        if (downloadSheetUrl === undefined) {
            return
        }

        if(props.count > 5000){
            alert('갯수 한도 (5000개) 를 초과하여 조회할 수 없습니다. 조건을 다시 설정해주세요.')
            return
        }

        console.log(props.count)

        setIsLoading(true);

        fetch(downloadSheetUrl(props.searchConditions, props.sortOrders, props.offset, props.count))
            .then(async value =>  await value.blob())
            .then(blob => window.URL.createObjectURL(blob))
            .then(url => {
                const a = document.createElement('a')
                a.href = url
                a.download = `${currentSubDomain}-${getExcelFileDate()}_all.xlsx`
                a.click()
            })
            .finally(()=>setIsLoading(false))


    }

    const onUploadSheetClick = (upload: SheetButtonUpload) => {
        uploadUrlState.setValue(upload)
        inputRef.current?.click()
    }

    const onDownloadExampleClick = (example: SheetButtonDownloadExample) => {
        setIsLoading(true);
        fetch(example.url)
            .then(async value => await value.blob())
            .then(blob => window.URL.createObjectURL(blob))
            .then(url => {
                const a = document.createElement('a')
                a.href = url
                a.download = `${example.name}.xlsx`
                a.click()
            })
        .finally(()=>setIsLoading(false))
    }

    const onExportExcelClick = () => {
        if(excelExportUrl === undefined){
            return
        }

        if(props.count > 5000){
            alert('갯수 한도 (5000개) 를 초과하여 조회할 수 없습니다. 조건을 다시 설정해주세요.')
            return
        }

        const memo = '검색조건)'+props.searchConditions.map((v)=>{
            return `${v.name}=${v.value}=${v.condition}`
        }).join(',')+' 정렬조건)'+ props.sortOrders.map((v)=>{
            return `${v.name}=${v.order}`
        }).join(',')

        const dto : ShoppingExcelExportDataTransferObject.Post =
            {memo:memo}

        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        setIsLoading(true);
        fetch(excelExportUrl(props.searchConditions, props.sortOrders, 0n, props.count),{
            method: 'POST',
            headers: headers,
            body: JSON.stringify(dto)
        })
            .then(value => {
                console.log(value)
                window.alert("엑셀 다운로드 요청 보내기 성공!")
            })
            .catch(err => console.error(err))
            .finally(()=>setIsLoading(false))
    }

    return <>
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                <Button icon={<DocumentOnePageSparkleFilled/>}>시트 옵션</Button>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>시트 옵션</DialogTitle>
                    <DialogContent>
                        {
                            isLoading ?
                                <Spinner />
                                :
                                <Column>
                                    <input
                                        hidden
                                        ref={inputRef}
                                        type={'file'}
                                        onChange={onFileChange}
                                        accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}/>
                                    <Button
                                        className={styles.button}
                                        appearance={'subtle'}
                                        icon={<DocumentArrowDown20Filled/>}
                                        onClick={onDownloadSheetClick}
                                        disabled={downloadSheetUrl === undefined}>
                                        현재 페이지 다운로드
                                    </Button>
                                    <input
                                        hidden
                                        ref={inputRef}
                                        type={'file'}
                                        onChange={onFileChange}
                                        accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}/>
                                    <Button
                                        className={styles.button}
                                        appearance={'subtle'}
                                        icon={<DocumentArrowDown20Filled/>}
                                        onClick={onDownloadAllSheetClick}
                                        disabled={downloadSheetUrl === undefined}>
                                        전체 페이지 다운로드
                                    </Button>
                                    {uploadSheetUrls?.map((upload, index) => (
                                        <Button
                                            key={`sheet-button-upload-${index}`}
                                            className={styles.button}
                                            appearance={'subtle'}
                                            icon={<DocumentArrowDown20Filled/>}
                                            onClick={() => onUploadSheetClick(upload)}>
                                            {upload.name}
                                        </Button>
                                    ))}
                                    {downloadSheetExampleUrls?.map((example, index) => (
                                        <Button
                                            key={`sheet-button-download-example-${index}`}
                                            className={styles.button}
                                            appearance={'subtle'}
                                            icon={<DocumentArrowDown20Regular/>}
                                            onClick={() => onDownloadExampleClick(example)}>
                                            {example.name}
                                        </Button>
                                    ))}
                                    <Button
                                        className={styles.button}
                                        appearance={'subtle'}
                                        icon={<DocumentArrowDown20Filled/>}
                                        onClick={onExportExcelClick}
                                        disabled={excelExportUrl === undefined}>
                                        엑셀 다운로드 비동기 요청
                                    </Button>
                                </Column>
                        }

                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button>취소</Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    </>
}